import * as React from "react";
import { toast, Modal, ModalFooter } from "@grandvisionhq/common";
import { Util, CheckboxWithLabel } from "@grandvisionhq/forms";
import { H2, Alert } from "@grandvisionhq/iris-ui";
import { useIntl } from "@grandvisionhq/state";
import { pushLoginExpressEditUserDetailsClickEventToDataLayer } from "../../../../../../../../data-layer.js";
import { seedShippingForm, seedInitialFormState } from "../../../../../../../../StepShipping/mappers/StepShipping/mappers/seed-shipping-form.js";
import { formStatesToValues } from "../../../../../../../utils/LoginExpressCheckout/utils/form-state-to-values.js";
import { isFormDataValid } from "../../../../../../../utils/LoginExpressCheckout/utils/validate-form-data.js";
import { useLoggedInCheckout } from "../../../../../../../context/LoginExpressCheckout/context/context.js";
import { useFormSlotWithoutRedux } from "../../../../../../../../slots/forms/slots/forms/form-context.js";
import { TitleWithIcon } from "../../../../../../../../components/TitleWithIcon/components/TitleWithIcon/TitleWithIcon.js";
import { useCheckoutSettings } from "../../../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
import { useBaseSummarySlot } from "../../../../../../../../slots/summaries/slots/summaries/summary-context.js";
const AddressSummary = ({
  address,
  onSubmit,
  isPickupOrder = false,
  isOrderDetailsValid,
  deliveryAddress: deliveryAddressDetails,
  handleAddressEqual,
  isAddressesEqual
}) => {
  const { getLabel, country } = useIntl();
  const labels = {
    title: getLabel("checkout.generic.title"),
    save: getLabel("common.actions.save")
  };
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const addressFormRef = React.useRef(null);
  const { formFieldsMap, shipping } = useCheckoutSettings();
  const { isUserLoggedIn } = useLoggedInCheckout();
  const [billingAddressFormData, setBillingAddressFormData] = React.useState({
    formState: {},
    formName: "",
    formValid: false
  });
  const [deliveryAddressFormData, setDeliveryAddressFormData] = React.useState(
    { formState: {}, formName: "", formValid: false }
  );
  const { Slots } = useBaseSummarySlot();
  const fieldMapForBillingAddress = React.useMemo(
    () => formFieldsMap?.billingAddress?.reduce((acc, item) => {
      acc.push(item.field);
      return acc;
    }, []) || [],
    [formFieldsMap?.billingAddress]
  );
  const fieldMapForDeliveryAddress = React.useMemo(
    () => formFieldsMap?.deliveryAddress?.reduce((acc, item) => {
      acc.push(item.field);
      return acc;
    }, []) || [],
    [formFieldsMap?.deliveryAddress]
  );
  const filledBillingFormData = React.useMemo(
    () => fieldMapForBillingAddress && address && seedShippingForm(fieldMapForBillingAddress, address),
    [address, fieldMapForBillingAddress]
  );
  const filledShippingFormData = React.useMemo(() => {
    if (fieldMapForDeliveryAddress && deliveryAddressDetails) {
      return seedShippingForm(fieldMapForDeliveryAddress, deliveryAddressDetails);
    }
    return fieldMapForDeliveryAddress ? seedInitialFormState(fieldMapForDeliveryAddress) : void 0;
  }, [deliveryAddressDetails, fieldMapForDeliveryAddress]);
  const isAddressComplete = React.useMemo(() => {
    if (formFieldsMap?.billingAddress?.length && !address || !isAddressesEqual && !deliveryAddressDetails) {
      return false;
    } else if (isAddressesEqual) {
      return formFieldsMap?.billingAddress ? isFormDataValid(formFieldsMap?.billingAddress, address) ?? false : false;
    } else {
      return formFieldsMap?.deliveryAddress ? isFormDataValid(formFieldsMap?.deliveryAddress, deliveryAddressDetails) ?? false : false;
    }
  }, [
    address,
    isAddressesEqual,
    deliveryAddressDetails,
    formFieldsMap?.billingAddress,
    formFieldsMap?.deliveryAddress
  ]);
  const handleSubmit = React.useCallback(() => {
    if (isAddressesEqual && !billingAddressFormData.formValid || !isAddressesEqual && (!billingAddressFormData.formValid || !deliveryAddressFormData.formValid)) {
      Util.focusToTopErrorFieldOnScreen();
      return;
    }
    const updatedBillingAddress = billingAddressFormData.formState && formStatesToValues(billingAddressFormData.formState);
    const updatedDeliveryAddress = deliveryAddressFormData.formState && formStatesToValues(deliveryAddressFormData.formState);
    const formData = {};
    if (billingAddressFormData.formValid) {
      formData.billingAddress = updatedBillingAddress;
    }
    if (!isAddressesEqual && deliveryAddressFormData.formValid) {
      formData.deliveryAddress = updatedDeliveryAddress;
    }
    if (shipping?.requiredShippingCountry && Boolean(formData.billingAddress?.country) && shipping?.requiredShippingCountry !== formData.billingAddress?.country) {
      toast.failed("checkout.address.requiredShippingCountry.errorMessage");
    } else {
      onSubmit?.(formData);
      setIsModalOpen(false);
    }
  }, [
    isAddressesEqual,
    billingAddressFormData,
    deliveryAddressFormData.formState,
    deliveryAddressFormData.formValid,
    onSubmit,
    shipping?.requiredShippingCountry
  ]);
  const onChangeHandler = React.useCallback(
    (formData) => {
      if (formData.formName == "billingAddress") {
        setBillingAddressFormData(formData);
        if (formData.formState && formData.formState.country?.value && formData.formState.country.value !== country) {
          handleAddressEqual(false);
        }
      }
      if (formData.formName == "deliveryAddress") setDeliveryAddressFormData(formData);
    },
    [country, handleAddressEqual]
  );
  const onEditHandler = React.useCallback(() => {
    pushLoginExpressEditUserDetailsClickEventToDataLayer({
      editType: isAddressComplete ? "edit_address" : "add_address_alert",
      isLoggedIn: isUserLoggedIn
    });
    setIsModalOpen(true);
  }, [isAddressComplete, isUserLoggedIn]);
  const { billingAddress: billingAddressForm, deliveryAddress: deliveryAddressForm } = useFormSlotWithoutRedux({
    formInitialData: {
      ...filledBillingFormData?.formData ? { billingAddress: filledBillingFormData?.formData } : {},
      ...filledShippingFormData?.formData ? { deliveryAddress: filledShippingFormData?.formData } : {}
    },
    className: "login-express-checkout__form-modal__form",
    formRef: addressFormRef,
    onChange: onChangeHandler
  });
  if (!Slots.addresses) return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Modal,
    {
      isOpen: isModalOpen,
      onModalClose: () => setIsModalOpen(false),
      showIcon: true,
      className: "login-express-checkout__form-modal",
      footer: /* @__PURE__ */ React.createElement(
        ModalFooter,
        {
          ctaCaption: getLabel("common.actions.save"),
          onCta: handleSubmit,
          onCancel: () => setIsModalOpen(false)
        }
      )
    },
    /* @__PURE__ */ React.createElement(H2, { variant: "body-head" }, labels.title),
    /* @__PURE__ */ React.createElement(React.Fragment, null, billingAddressForm),
    !isPickupOrder && /* @__PURE__ */ React.createElement(
      CheckboxWithLabel,
      {
        id: "usingDeliveryAddress",
        value: "usingDeliveryAddress",
        checked: Boolean(isAddressesEqual),
        onChange: (state) => handleAddressEqual(state.checked),
        className: "billing-and-delivery-form__checkbox",
        label: getLabel("checkout.shipment.differentDeliveryAddress")
      }
    ),
    !isAddressesEqual && !isPickupOrder && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(TitleWithIcon, { icon: "location-pin", label: getLabel("checkout.deliveryAddress") }), deliveryAddressForm)
  ), /* @__PURE__ */ React.createElement(
    Slots.addresses,
    {
      actions: {
        onEdit: onEditHandler
      },
      values: {
        isAddressesEqual,
        isPickupOrder,
        billingAddress: address,
        deliveryAddress: deliveryAddressDetails
      },
      errorNotification: !isAddressComplete ? /* @__PURE__ */ React.createElement(
        Alert,
        {
          className: "alert-m-default ",
          variant: isOrderDetailsValid ? "info" : "error",
          title: getLabel("checkout.address.incomplete.title")
        },
        getLabel("checkout.information.incomplete.message")
      ) : null
    }
  ));
};
export {
  AddressSummary
};
//# sourceMappingURL=AddressSummary.js.map
